import type { ContentItemWithCollectionsModel } from '@domain/models/ContentItemWithCollectionsModel';
import type { CollectionModel } from '@domain/models/CollectionModel';
import type { CreatorModel } from '@domain/models/CreatorModel';
import type { BenefitFaq, BenefitTopic } from '@presentation/content/types';

export const SITE_ORIGIN =
	import.meta.env.VITE_ENVIRONMENT === 'production' ? 'https://jpu.app' : 'https://dev.jpu.app';

const BRAND = 'Jigsaw Puzzle Universe';
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const SITE_ID = `${SITE_ORIGIN}/#website`;

export const absoluteUrl = (path: string): string => `${SITE_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;

type JsonLdNode = Record<string, unknown>;

export type BreadcrumbItem = { name: string; url?: string };

// Global WebSite + Organization graph — emitted on every page (see +Head.tsx).
export const buildGlobalGraph = (): JsonLdNode => ({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'Organization',
			'@id': ORG_ID,
			name: BRAND,
			url: SITE_ORIGIN,
			logo: absoluteUrl('/favicon.ico'),
		},
		{
			'@type': 'WebSite',
			'@id': SITE_ID,
			name: BRAND,
			url: SITE_ORIGIN,
			publisher: { '@id': ORG_ID },
		},
	],
});

export const buildBreadcrumb = (items: BreadcrumbItem[]): JsonLdNode => ({
	'@context': 'https://schema.org',
	'@type': 'BreadcrumbList',
	itemListElement: items.map((item, index) => ({
		'@type': 'ListItem',
		position: index + 1,
		name: item.name,
		...(item.url ? { item: absoluteUrl(item.url) } : {}),
	})),
});

const interactionStat = (type: string, value?: number | null): JsonLdNode | null =>
	value
		? {
				'@type': 'InteractionCounter',
				interactionType: `https://schema.org/${type}`,
				userInteractionCount: value,
		  }
		: null;

export const buildPuzzleGraph = (puzzle: ContentItemWithCollectionsModel, path: string): JsonLdNode => {
	const url = absoluteUrl(path);
	const author = puzzle.author;
	const stats = [interactionStat('LikeAction', puzzle.likes), interactionStat('PlayAction', puzzle.plays)].filter(
		Boolean
	);

	return {
		'@context': 'https://schema.org',
		'@type': 'VisualArtwork',
		'@id': `${url}#puzzle`,
		name: `${puzzle.name} jigsaw puzzle`,
		url,
		artform: 'Jigsaw puzzle',
		image: {
			'@type': 'ImageObject',
			contentUrl: puzzle.imageSrc,
			caption: `${puzzle.name} jigsaw puzzle${author?.name ? ` by ${author.name}` : ''}`,
		},
		...(puzzle.keywords?.length ? { keywords: puzzle.keywords.join(', ') } : {}),
		...(author
			? {
					author: {
						'@type': 'Person',
						name: author.name,
						url: absoluteUrl(`/${author.profileLink}`),
					},
			  }
			: {}),
		...(puzzle.publishedAt ? { datePublished: new Date(puzzle.publishedAt).toISOString() } : {}),
		...(stats.length ? { interactionStatistic: stats } : {}),
		isPartOf: { '@id': SITE_ID },
		publisher: { '@id': ORG_ID },
	};
};

export const buildCreatorGraph = (creator: CreatorModel, path: string): JsonLdNode => {
	const url = absoluteUrl(path);

	return {
		'@context': 'https://schema.org',
		'@type': 'ProfilePage',
		'@id': `${url}#profile`,
		url,
		mainEntity: {
			'@type': 'Person',
			name: creator.name,
			url,
			...(creator.avatarSrc ? { image: creator.avatarSrc } : {}),
			...(creator.puzzlesCount
				? {
						interactionStatistic: {
							'@type': 'InteractionCounter',
							interactionType: 'https://schema.org/FollowAction',
							userInteractionCount: creator.followersCount,
						},
				  }
				: {}),
		},
		isPartOf: { '@id': SITE_ID },
	};
};

export const buildCategoryGraph = (category: CollectionModel, path: string): JsonLdNode => {
	const url = absoluteUrl(path);

	return {
		'@context': 'https://schema.org',
		'@type': 'CollectionPage',
		'@id': `${url}#collection`,
		url,
		name: `${category.name} jigsaw puzzles`,
		...(category.imageSrc ? { image: category.imageSrc } : {}),
		...(category.numberOfPuzzles
			? {
					mainEntity: {
						'@type': 'ItemList',
						numberOfItems: category.numberOfPuzzles,
					},
			  }
			: {}),
		isPartOf: { '@id': SITE_ID },
	};
};

export const buildArticleGraph = (topic: BenefitTopic, path: string): JsonLdNode => {
	const url = absoluteUrl(path);

	return {
		'@context': 'https://schema.org',
		'@type': 'Article',
		'@id': `${url}#article`,
		headline: topic.title,
		description: topic.intro,
		url,
		mainEntityOfPage: url,
		...(topic.generatedAt ? { datePublished: topic.generatedAt, dateModified: topic.generatedAt } : {}),
		author: { '@id': ORG_ID },
		publisher: { '@id': ORG_ID },
		isPartOf: { '@id': SITE_ID },
		...(topic.studies?.length
			? {
					citation: topic.studies.map((study) => ({
						'@type': 'ScholarlyArticle',
						name: study.title,
						url: study.url,
						...(study.journal ? { isPartOf: { '@type': 'Periodical', name: study.journal } } : {}),
						...(study.year ? { datePublished: study.year } : {}),
					})),
			  }
			: {}),
	};
};

export const buildFaqGraph = (faqs: BenefitFaq[]): JsonLdNode => ({
	'@context': 'https://schema.org',
	'@type': 'FAQPage',
	mainEntity: faqs.map((faq) => ({
		'@type': 'Question',
		name: faq.question,
		acceptedAnswer: { '@type': 'Answer', text: faq.answer },
	})),
});

export const buildAboutGraph = (path: string): JsonLdNode => ({
	'@context': 'https://schema.org',
	'@type': 'AboutPage',
	'@id': `${absoluteUrl(path)}#about`,
	url: absoluteUrl(path),
	name: `About ${BRAND}`,
	mainEntity: { '@id': ORG_ID },
	isPartOf: { '@id': SITE_ID },
	mentions: [
		{
			'@type': 'SoftwareApplication',
			name: 'Primio',
			url: 'https://primio.dev',
			applicationCategory: 'DeveloperApplication',
		},
	],
});
